import * as Fullstory from "@fullstory/browser";
import { hasIn } from "lodash";

import { init, listen } from "../units/launch-darkly";
import { register, setUserProperties } from "../units/tracking";
import { LD_CONTEXT_KINDS, LD_USER_DEFAULT_CONTEXT } from "../constants";

import { pickEnvInfo } from "./selectors";

import type { LDFlagSet } from "launchdarkly-js-client-sdk";

const SET = "feature-flags/SET";

// control flags on https://app.launchdarkly.com/
export const flagDefaults = {
  easypay: false,
  "ipm-rescind-pending-cancellation": false,
  "ipm-resolve-inspection-conditions": false,
  "ipm-cancel-insured": false,
  "ipm-change-customer": false,
  "ipm-additional-interest": false,
  "live-chat": false,
  "quote-quick-start-commercial": false,
  mixpanel: true,
  "manage-whats-new": false,
  "ix-auth": false,
  "agency-manager": true,
  "tools-page-access": false,
  "tools-flash-enabler": false,
  "tools-quote-bridge": false,
  "make-payment-override-mindue": false,
  "make-payment-cancelled-policy": false,
  "make-payment-v2": false,
  "feature-compare-quote": false,
  cobrowsing: false,
  resources: "classic",
  "property-hazard": true,
  "invoice-amount-payment-option": false,
  "liftlock-ruleset-ids": []
};

// Ability to override feature flags for testing purposes
const FLAG_OVERRIDE_URL_PARAM = "flag_overrides";
export const FEATURE_FLAG_LOCALSTORAGE_KEY = "featureFlags";

const getFlagOverrides = () => {
  const params = new URLSearchParams(document.location.search);
  if (params.has(FLAG_OVERRIDE_URL_PARAM)) {
    let overrides;
    try {
      overrides = JSON.parse(params.get(FLAG_OVERRIDE_URL_PARAM));
    } catch {
      // eslint-disable-next-line no-console
      console.warn(
        `Unable to apply flag_overrides from url params. Please check the formatting of your overrides: ${params.get(
          FLAG_OVERRIDE_URL_PARAM
        )}`
      );
      overrides = {};
    }
    return overrides;
  } else return {};
};

/**
 * Adds user's featureflag selections to localStorage so they can be persisted
 * accross page refreshes (see `updateFlags` around LOC-150 in this module)
 * @param {{[key]: boolean}} flags the ID of a feature-flag and its status on/off
 * @return void This function has a side effect of editing localStorage
 */
export const persistFeatureFlagsToLocalStorage = (flags = {}) => {
  const featureFlagsInLocalStorage = JSON.parse(
    localStorage.getItem(FEATURE_FLAG_LOCALSTORAGE_KEY)
  );
  if (!featureFlagsInLocalStorage) {
    localStorage.setItem(FEATURE_FLAG_LOCALSTORAGE_KEY, JSON.stringify(flags));
  } else {
    localStorage.setItem(
      FEATURE_FLAG_LOCALSTORAGE_KEY,
      JSON.stringify({ ...featureFlagsInLocalStorage, ...flags })
    );
  }
};

export const clearFeatureFlagsInLocalStorage = dispatch => {
  localStorage.removeItem(FEATURE_FLAG_LOCALSTORAGE_KEY);
  // eslint-disable-next-line no-undef
  dispatch(initFeatureFlags(CONFIG.envConfig.launchDarklyID));
};

export const removeFeatureFlagFromLocalStorage = (key: string) => {
  const featureFlagsInLocalStorage = JSON.parse(
    localStorage.getItem(FEATURE_FLAG_LOCALSTORAGE_KEY)
  );
  if (hasIn(featureFlagsInLocalStorage, key)) {
    delete featureFlagsInLocalStorage[key];
  }
  if (Object.keys(featureFlagsInLocalStorage).length === 0) {
    localStorage.removeItem(FEATURE_FLAG_LOCALSTORAGE_KEY);
  } else {
    localStorage.setItem(
      FEATURE_FLAG_LOCALSTORAGE_KEY,
      JSON.stringify({ ...featureFlagsInLocalStorage })
    );
  }
};

export const hasFeatureFlagInLocalStorage = (key: string) => {
  const featureFlagsInLocalStorage = JSON.parse(
    localStorage.getItem(FEATURE_FLAG_LOCALSTORAGE_KEY)
  );
  return hasIn(featureFlagsInLocalStorage, key);
};

export const setFeatureFlags = (flags = {}) => {
  const flagOverrides = getFlagOverrides();

  return {
    type: SET,
    flags: { ...flags, ...flagOverrides }
  };
};

const updateTrackingInfo = (dispatch, getState) => {
  const { featureFlags: flags } = getState();

  const featureFlagsAsArrayOfStrings = Object.entries(flags).map(
    ([name, activeVariant]) => `${name}/${JSON.stringify(activeVariant)}`
  );
  setUserProperties({
    featureFlags: featureFlagsAsArrayOfStrings
  });
  register({
    featureFlags: featureFlagsAsArrayOfStrings
  });

  // These vars are set by init from @fullstory/browser
  // https://github.com/fullstorydev/fullstory-browser-sdk/blob/main/src/index.js
  const isFullstoryInitialized = !!window._fs_initialized;
  const isFullstoryInDevMode = !!window._fs_dev_mode;
  if (isFullstoryInitialized && !isFullstoryInDevMode) {
    Fullstory.setUserVars({ featureFlags_strs: featureFlagsAsArrayOfStrings });
  }
};

/**
 * Within this function, flags are accessed from localStorage and then appended to flags loaded for the user from launchdarkly.
 * This allows a user to use a flag, refresh their browser, and still see the flag in use after refresh
 * @param {{[key]: boolean}} flags flags to which properties can be added, or changed to/from true & false.
 * @return {{[key]: boolean}} flags
 */
const addFeatureFlagsFromLocalStorageToAppState = (flags: LDFlagSet) => {
  const localStorageFeatureFlags = JSON.parse(
    localStorage.getItem(FEATURE_FLAG_LOCALSTORAGE_KEY)
  );
  if (!localStorageFeatureFlags) return flags;

  return { ...flags, ...localStorageFeatureFlags };
};

export const initFeatureFlags = launchDarklyKey => (dispatch, getState) => {
  const {
    email,
    fullname,
    roles,
    alc,
    affiliation,
    channel,
    username,
    isAdmin,
    isBackoffice,
    loginType,
    organizationID,
    org: { pupStatus }
  } = pickEnvInfo(getState());

  const updateFlags = (flags: LDFlagSet) => {
    dispatch(setFeatureFlags(flags));
    dispatch(updateTrackingInfo);
  };

  const userContext = {
    kind: LD_CONTEXT_KINDS.USER,
    key: toLower(username),
    email: toLower(email),
    name: toLower(fullname),
    roles: toUpper(roles),
    alc: toUpper(alc),
    affiliation: toUpper(affiliation),
    channel: toUpper(channel),
    isAdmin,
    isBackoffice,
    loginType,
    organizationID: toLower(organizationID),
    pupStatus
  };

  return init({
    key: launchDarklyKey,
    context: userContext
  })
    .then((flags: LDFlagSet) => {
      // check localstorage and combine feature flags there with flags from LD
      const result = addFeatureFlagsFromLocalStorageToAppState(flags);
      updateFlags(result);
    })
    .then(() => {
      listen(updateFlags);
    });
};

export const initFeatureFlagsForLogin =
  (launchDarklyKey: string) => dispatch => {
    const updateFlags = (flags: LDFlagSet) => {
      dispatch(setFeatureFlags(flags));
      // dispatch(updateTrackingInfo);
    };

    return init({
      key: launchDarklyKey,
      context: LD_USER_DEFAULT_CONTEXT
    })
      .then((flags: LDFlagSet) => {
        // check localstorage and combine feature flags there with flags from LD
        const result = addFeatureFlagsFromLocalStorageToAppState(flags);
        updateFlags(result);
      })
      .then(() => {
        listen(updateFlags);
      });
  };

export const featureFlags = (state = flagDefaults, { type, flags }) => {
  const updatedFeatureFlags = type === SET ? { ...state, ...flags } : state;
  return updatedFeatureFlags;
};

function toLower(stringOrArray: string | string[]) {
  if (Array.isArray(stringOrArray)) {
    return stringOrArray.map(item => toLower(item));
  }
  if (typeof stringOrArray === "string") {
    return stringOrArray.toLowerCase();
  }
  return stringOrArray;
}

function toUpper(stringOrArray: string | string[]) {
  if (Array.isArray(stringOrArray)) {
    return stringOrArray.map(item => toUpper(item));
  }
  if (typeof stringOrArray === "string") {
    return stringOrArray.toUpperCase();
  }
  return stringOrArray;
}
